'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useComponentVisible } from '@/hooks/visibility';
import type { ServerLink } from '../helpers';
import { MenuLinkClientWrapper } from './MenuLinkClientWrapper';
import { ScMenuMore, ScMenuMoreDropdown } from './styled';

const DROPDOWN_OFFSET = 8;

interface MenuMoreProps {
  links: ServerLink[];
  hideConditionResults: Record<string, boolean>;
}

export const MenuMore = ({ links, hideConditionResults }: MenuMoreProps) => {
  const currentPage = usePathname();
  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const handleClick = () => setIsComponentVisible(false);

  const handleToggle = () => {
    const trigger = openerRef.current?.getBoundingClientRect();

    if (trigger)
      setPosition({
        top: trigger.bottom + DROPDOWN_OFFSET,
        left: trigger.left + trigger.width / 2,
      });

    setIsComponentVisible(!isComponentVisible);
  };

  useEffect(() => {
    if (!isComponentVisible) return;

    const close = () => setIsComponentVisible(false);

    window.addEventListener('scroll', close);
    window.addEventListener('resize', close);

    return () => {
      window.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
    };
  }, [isComponentVisible, setIsComponentVisible]);

  const isActive = links.some(({ url }) => url === currentPage);

  return (
    <ScMenuMore $isActive={isActive} className="menu-more">
      <button
        aria-label="more"
        onClick={handleToggle}
        ref={openerRef}
        type="button"
      >
        ...
      </button>

      {isComponentVisible && (
        <ScMenuMoreDropdown
          $left={position.left}
          $top={position.top}
          onClick={handleClick}
          ref={ref}
        >
          {links.map(({ id, url, hideConditions, serverComponent }) => (
            <MenuLinkClientWrapper
              hideConditionResults={hideConditionResults}
              hideConditions={hideConditions}
              id={id}
              key={id}
              url={url}
            >
              {serverComponent}
            </MenuLinkClientWrapper>
          ))}
        </ScMenuMoreDropdown>
      )}
    </ScMenuMore>
  );
};
